type KeywordToken = {
  type: "OpenBrace" | "CloseBrace" | "Dot";
  start: number;
  end: number;
};

type TextToken = {
  type: "Text";
  value: string;
  start: number;
  end: number;
};

export type Token = KeywordToken | TextToken;

/**
 * Does a first pass of the tokens to insert a `CloseBrace` token if one is missing for the sake of parsing the program.
 * It will insert a `CloseBrace` at the end if, and only if, there is one more `OpenBrace` than `CloseBrace`
 * and the there aren't any additional `OpenBraces` before the last `CloseBrace`.
 */
const fixMissingCloseBrace = (tokens: Token[]): Token[] => {
  let openCloseDiff = 0;

  for (const token of tokens) {
    if (token.type === "OpenBrace") {
      openCloseDiff++;
    } else if (token.type === "CloseBrace") {
      openCloseDiff--;
    }

    /**
     * If at any point there is more than one `OpenBrace` than `CloseBrace`,
     * then we should bail out because it's not a valid expression anyway.
     */
    if (openCloseDiff > 1 || openCloseDiff < 0) {
      return tokens;
    }
  }

  if (openCloseDiff === 1) {
    const last = tokens[tokens.length - 1];

    const token = {
      type: "CloseBrace",
      start: last.end,
      end: last.end + 1,
    } as const;

    return [...tokens, token];
  }

  return tokens;
};

/**
 * Splits a string of text into tokens.
 * Meaningful tokens are `${`, `}`, `.` and any text in between.
 * Keeps track of the positions of each token in the original string.
 */
export const tokenize = (text: string): Token[] => {
  const tokens: Token[] = [];

  let token: Token | null = null;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === "$") {
      if (text[i + 1] === "{") {
        if (token) {
          tokens.push(token);
        }

        token = {
          type: "OpenBrace",
          start: i,
          end: i + 2,
        };

        tokens.push(token);
        token = null;

        i++;
      } else if (token) {
        token.value += char;
        token.end = i + 1;
      } else {
        token = {
          type: "Text",
          value: char,
          start: i,
          end: i + 1,
        };
      }
    } else if (char === "}") {
      if (token) {
        tokens.push(token);
      }

      token = {
        type: "CloseBrace",
        start: i,
        end: i + 1,
      };

      tokens.push(token);
      token = null;
    } else if (char === ".") {
      if (token) {
        tokens.push(token);
      }

      token = {
        type: "Dot",
        start: i,
        end: i + 1,
      };

      tokens.push(token);
      token = null;
    } else if (token) {
      token.value += char;
      token.end = i + 1;
    } else {
      token = {
        type: "Text",
        value: char,
        start: i,
        end: i + 1,
      };
    }
  }

  if (token) {
    tokens.push(token);
  }

  return fixMissingCloseBrace(tokens);
};
